import { useState } from "react";
import { useTranslation } from "react-i18next";

import StatePicker from "@/components/StatePicker";
import type { UsStateCode } from "@/lib/states";

/**
 * apps/web :: components/onboarding :: TargetStatesStep
 *
 * Step 3 of the wizard. Thin wrapper around `<StatePicker mode="multi" />`
 * with maxSelections=5 (mirrors the backend `MAX_TARGET_STATES` constant).
 * The parent receives the UsStateCode[] selection on Next.
 */
export interface TargetStatesStepProps {
  value: UsStateCode[];
  onNext: (states: UsStateCode[]) => void;
  onBack: () => void;
}

export default function TargetStatesStep({
  value,
  onNext,
  onBack,
}: TargetStatesStepProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<UsStateCode[]>(value);

  const canProceed = draft.length >= 1;

  return (
    <div className="space-y-md">
      <header>
        <h2 className="font-display text-xl font-semibold text-brand-primary">
          {t("onboarding.targetStates.title")}
        </h2>
        <p className="mt-xs text-sm text-neutral-700">
          {t("onboarding.targetStates.body")}
        </p>
      </header>

      <StatePicker
        mode="multi"
        maxSelections={5}
        value={draft}
        onChange={(next) => {
          if (Array.isArray(next)) setDraft(next);
        }}
        aria-label={t("onboarding.targetStates.groupLabel")}
      />

      <div className="flex items-center justify-between gap-sm pt-md">
        <button
          type="button"
          data-testid="onboarding-back"
          onClick={onBack}
          className="inline-flex items-center justify-center rounded-md border border-brand-primary/20 bg-white px-md py-sm text-sm font-medium text-brand-primary transition-colors hover:border-brand-primary/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
        >
          {t("onboarding.back")}
        </button>
        <button
          type="button"
          data-testid="onboarding-next"
          disabled={!canProceed}
          onClick={() => onNext(draft)}
          className="inline-flex items-center justify-center rounded-md bg-brand-primary px-md py-sm text-sm font-semibold text-white transition-colors hover:bg-brand-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary disabled:cursor-not-allowed disabled:opacity-50"
        >
          {t("onboarding.next")}
        </button>
      </div>
    </div>
  );
}
